import { useState } from 'react';
import { NavBar } from '../../components/NavBar.tsx';
import { Alert } from '../../components/Overlay.tsx';
import { Icon } from '../../components/Icon.tsx';
import { ShareSheet } from './ShareSheet.tsx';
import { Seg } from '../../components/Seg.tsx';
import { ErrorState } from '../../components/DataState.tsx';
import { ApiError } from '../../api/client.ts';
import { deleteNote } from '../../api/notes.ts';
import {
  CATEGORIES,
  MAX_REPRESENTATIVE,
  PRAYER_STATUS,
  PRIORITY,
  priorityLabel,
  statusLabel,
} from '../../domain.ts';
import type { PrayerNote } from '../../domain.ts';
import type { NotePatch } from '../../api/notes.ts';
import { today } from './shared.ts';

/**
 * C4 · C4-1 — 기도 노트 설정.
 *
 * 기본 화면과 바꿀 수 있는 항목이 다릅니다. 여기서만 영역 · 상태 ·
 * 중요도 · PLAN · 대표 · Private 를 바꾸고 공유와 삭제도 여기 있습니다.
 *
 * 고르는 즉시 저장되므로 «저장» 버튼이 없습니다. 대신 실패했을 때 화면이
 * 조용히 예전 값으로 돌아가면 안 되므로, 거절당한 이유를 그 자리에 띄웁니다.
 */

interface Props {
  note: PrayerNote;
  /** 이미 대표로 지정된 개수 — C4-1 을 띄울지 판단하고 안내에도 씁니다 */
  representativeCount: number;
  /**
   * 작성자 이름. 노트에는 authorId(UUID)만 들어 있어 그대로는 못 보여 줍니다.
   * 지금은 내 노트만 열 수 있으므로 언제나 나입니다 — 공유가 붙으면 달라집니다.
   */
  author: string;
  onBack: () => void;
  onPatch: (changes: NotePatch) => Promise<boolean>;
  onDeleted: () => void;
}

export function NoteSettings({
  note,
  representativeCount,
  author,
  onBack,
  onPatch,
  onDeleted,
}: Props) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sharing, setSharing] = useState(false);
  // 대표 상한에 걸렸을 때만 뜨는 알리기 전용 팝업 (C4-1)
  const [capped, setCapped] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const full = !note.representative && representativeCount >= MAX_REPRESENTATIVE;

  const toggleRepresentative = async () => {
    // 서버까지 갔다 와서 거절당하기 전에, 꽉 찬 것이 이미 보이면 여기서 알립니다.
    // 켜졌다가 되돌아오는 깜빡임이 사라집니다 — 서버 검사는 그대로 남아 있습니다.
    if (full) {
      setCapped(true);
      return;
    }
    if (!(await onPatch({ representative: !note.representative }))) setCapped(true);
  };

  return (
    <div className="screen">
      <NavBar
        title="기도 노트 설정"
        subtitle={note.category}
        start={{ icon: 'back', label: '이전', onClick: onBack }}
      />

      <div className="screen__body">
        {/* 노트를 다른 영역으로 옮기는 곳이 여기입니다. 고르는 즉시 옮겨지고
            따로 확인을 묻지 않습니다 — 되돌리기 쉬운 동작이라서입니다 */}
        <div className="field">
          <span className="field__label">영역</span>
          <Seg
            label="영역"
            options={CATEGORIES.map((c) => ({ value: c, label: c }))}
            value={note.category}
            onChange={(category) => void onPatch({ category })}
          />
        </div>

        <div className="field">
          <span className="field__label">기도 상태</span>
          {/* 방향이 없는 값이라 같은 칸을 다시 눌러도 뒤집히지 않습니다 */}
          <Seg
            label="기도 상태"
            options={PRAYER_STATUS.map((s) => ({ value: s, label: statusLabel[s] }))}
            value={note.status}
            onChange={(status) => void onPatch({ status })}
          />
        </div>

        <div className="field">
          <span className="field__label">중요도</span>
          <Seg
            label="중요도"
            options={PRIORITY.map((p) => ({ value: p, label: priorityLabel[p] }))}
            value={note.priority}
            crit="URGENT"
            onChange={(priority) => void onPatch({ priority })}
          />
        </div>

        <div className="field">
          <span className="field__label">Plan</span>
          <Seg
            label="Plan"
            options={[
              { value: 'EVERYDAY', label: 'Everyday' },
              { value: 'DATE', label: '날짜 지정' },
            ]}
            value={note.plan.type}
            onChange={(type) =>
              void onPatch({
                plan: type === 'EVERYDAY' ? { type: 'EVERYDAY' } : { type: 'DATE', date: today() },
              })
            }
          />
          {note.plan.type === 'DATE' && (
            <input
              className="input"
              type="date"
              value={note.plan.date}
              onChange={(e) => void onPatch({ plan: { type: 'DATE', date: e.target.value } })}
            />
          )}
        </div>

        <div className="field">
          <span className="field__label">대표 기도제목</span>
          <button
            type="button"
            className="toggle-row"
            aria-pressed={note.representative}
            onClick={toggleRepresentative}
          >
            <span className="toggle-row__label">
              대표로 지정
              <span className="toggle-row__sub">
                {MAX_REPRESENTATIVE}개 중 {representativeCount}개 사용 중 · 친구의 Partner 팝업에
                보입니다
              </span>
            </span>
            <span className={note.representative ? 'toggle toggle--on' : 'toggle'} />
          </button>
        </div>

        <div className="field">
          <span className="field__label">Private</span>
          <button
            type="button"
            className="toggle-row"
            aria-pressed={note.isPrivate}
            onClick={() => void onPatch({ isPrivate: !note.isPrivate })}
          >
            <span className="toggle-row__label">
              비공개
              <span className="toggle-row__sub">친구에게 공개 노트로 보이지 않습니다</span>
            </span>
            <span className={note.isPrivate ? 'toggle toggle--on' : 'toggle'} />
          </button>
        </div>

        {/* 자동 갱신이라 입력할 수 없다는 것을 색으로 구분합니다 */}
        <div className="card card--flat">
          <Field label="작성자" value={author} />
          <Field label="Created" value={note.createdAt.slice(0, 10).replace(/-/g, '.')} />
          <Field label="Updated" value={note.updatedAt.slice(0, 10).replace(/-/g, '.')} />
        </div>

        {failure && <ErrorState message={failure} onRetry={() => setFailure(null)} />}

        <div className="btn-row btn-row--foot">
          <button type="button" className="btn btn--primary" onClick={() => setSharing(true)}>
            <Icon name="share" size={16} />
            공유
          </button>
          <button
            type="button"
            className="btn btn--danger-quiet"
            onClick={() => setConfirmDelete(true)}
          >
            삭제
          </button>
        </div>
        <p className="hint">
          공유한 뒤 원본을 고치면 받은 쪽 화면에도 실시간으로 반영됩니다. 받은 사람은
          이모티콘과 코멘트를 남길 수 있습니다.
        </p>
      </div>

      {sharing && (
        <ShareSheet
          noteId={note.id}
          isPrivate={note.isPrivate}
          onClose={() => setSharing(false)}
        />
      )}

      {/* C4-1 — 알리기만 하는 팝업. 확인 · ✕ · 배경 셋 다 결과가 같습니다 */}
      {capped && (
        <Alert
          title={`대표 기도제목이 ${MAX_REPRESENTATIVE}개로 꽉 찼습니다`}
          onClose={() => setCapped(false)}
          actions={
            <button type="button" className="btn btn--primary" onClick={() => setCapped(false)}>
              확인
            </button>
          }
        >
          <p>
            대표는 최대 {MAX_REPRESENTATIVE}개까지 지정할 수 있습니다.
            <br />
            다른 기도제목을 먼저 해제한 뒤 다시 켜 주세요.
          </p>
        </Alert>
      )}

      {confirmDelete && (
        <Alert
          title={`「${note.title}」을 삭제할까요?`}
          onClose={() => setConfirmDelete(false)}
          actions={
            <>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setConfirmDelete(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="btn btn--danger"
                onClick={async () => {
                  try {
                    await deleteNote(note.id);
                    setConfirmDelete(false);
                    onDeleted();
                  } catch (e) {
                    setConfirmDelete(false);
                    setFailure(e instanceof ApiError ? e.message : '삭제하지 못했습니다.');
                  }
                }}
              >
                삭제
              </button>
            </>
          }
        >
          <p>
            진행 기록과 히스토리도 <b>함께 사라집니다.</b>
            <br />
            되돌릴 수 없습니다.
          </p>
        </Alert>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="kv">
      <span className="kv__k">{label}</span>
      <span className="kv__v">{value}</span>
    </div>
  );
}
